import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import { TrackableItem, UpdateTrackableItem } from 'types/trackables';

import { useAPI } from 'hooks/api/useAPI';
import { updateQueriesData } from 'services/update_queries_data';

type UseUpdateTrackableItemParams = {
  trackable_list_id: string;
  trackable_item_id: string;
  data: UpdateTrackableItem;
};

export function useUpdateTrackableItem({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<TrackableItem, APIError, UseUpdateTrackableItemParams> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();

  return useMutation(
    async ({ trackable_list_id, trackable_item_id, data }: UseUpdateTrackableItemParams) => {
      const response = await client.put(`/apps/${applicationID}/trackables/${trackable_list_id}/items/${trackable_item_id}`, data);
      return response.data;
    },
    {
      ...options,
      onMutate: async (params) => {
        onMutate?.(params);

        return updateQueriesData({
          queryClient,
          listQueryKey: ['trackable-items'],
          singleQueryKey: ['trackable-item'],
          ids: [params.trackable_item_id],
          updateData: (prevData: TrackableItem) => ({
            ...prevData,
            ...params.data,
            updated_at: new Date().toISOString(),
          }),
        });
      },
      onError: (error, params, context) => {
        onError?.(error, params, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled: (...args) => {
        onSettled?.(...args);

        queryClient.invalidateQueries(['trackable-items']);
        queryClient.invalidateQueries(['trackable-item']);
      },
    }
  );
}